import React, { useMemo } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  useTheme,
  withStyles,
} from "@material-ui/core";
import { ActionProps } from "./Action";
import { CustomDialog, CustomDialogProps } from "./CustomDialog";

// Variants
import { ErrorIcon, InfoIcon, WarningIcon } from "../../icons";

// Customised Padding
const CustomDialogTitle = withStyles(
  /* istanbul ignore next */ (theme) => ({
    root: {
      padding: theme.spacing(3),
    },
  }),
)(DialogTitle);

const CustomDialogContent = withStyles(
  /* istanbul ignore next */ (theme) => ({
    root: {
      padding: `0 ${theme.spacing(3)}px`,
    },
  }),
)(DialogContent);

const CustomDialogActions = withStyles(
  /* istanbul ignore next */ (theme) => ({
    root: {
      padding: `${theme.spacing(5)}px ${theme.spacing(3)}px ${theme.spacing(
        3,
      )}px`,
      // We inverse the render order so that the first actions={[]} array item is the right-most item rendered
      flexDirection: "row-reverse",
      justifyContent: "flex-start",
    },
    spacing: {
      "& > :not(:first-child)": {
        marginLeft: 0,
        marginRight: theme.spacing(1),
      },
    },
  }),
)(DialogActions);

export type SimpleDialogProps = {
  variant?: "default" | "warning" | "error" | "info";
  title: string;
  actions: React.ReactElement[];
} & CustomDialogProps;

export const SimpleDialog: React.FC<SimpleDialogProps> = ({
  variant = "default",
  title,
  actions,
  children,
  ...props
}) => {
  const theme = useTheme();

  const icon: null | React.ReactChild = useMemo(() => {
    switch (variant) {
      case "default":
        return null;
      case "error":
        return <ErrorIcon fill={theme.palette.error.main} />;
      case "info":
        return <InfoIcon fill={theme.palette.secondary.main} />;
      case "warning":
        return <WarningIcon fill={theme.palette.warning.main} />;
    }
  }, [
    theme.palette.error.main,
    theme.palette.secondary.main,
    theme.palette.warning.main,
    variant,
  ]);

  const renderActions = () => {
    const count = React.Children.count(actions);
    let hasRenderedPrimary = false;

    return React.Children.map(actions, (action, i) => {
      const props: ActionProps = {
        key: i,
        variant:
          // if we only have a single action, we force it being "primary"
          count === 1
            ? "primary"
            : // if we have many actions and we have already rendered 1 action
            // as primary, we must render this action as "secondary" even if
            // it has technically got the primary variant prop as only one
            // primary is allowed per dialog actions.
            action.props.variant === "primary" && hasRenderedPrimary
            ? undefined // undefined throws the Action component into "text" mode
            : action.props.variant,
      };

      if (action.props.variant === "primary") {
        hasRenderedPrimary = true;
      }

      return React.cloneElement(action, props);
    });
  };

  return (
    <CustomDialog {...props}>
      <CustomDialogTitle disableTypography={true}>
        <Box display="flex" alignItems="center">
          {icon ? (
            <Box mr={1} lineHeight={0}>
              {icon}
            </Box>
          ) : null}
          <Typography component="h2" variant="h6">
            {title}
          </Typography>
        </Box>
      </CustomDialogTitle>
      <CustomDialogContent>{children}</CustomDialogContent>
      <CustomDialogActions>{renderActions()}</CustomDialogActions>
    </CustomDialog>
  );
};
